import { PatientKind } from "common/types";
import { useLocation } from "common/router/hooks";
import { useAuthentication } from "common/auth/hooks";

import Tooltip from "client/common/components/content/Tooltip";
import TextButton from "client/common/components/buttons/TextButton";
import { useBreakpoints } from "client/common/hooks";
import { useAccountCreatePetDrawer } from "client/accounts/stackables";
import { ClientDetailPatient } from "client/accounts/types";
import { urls } from "client/routes";

import styles from "../styles/ForMyPetFilter.module.less";

const SCOPE_EXPLAINER = "Show only products that match your pet's species and weight.";

export type PetSearch = { patient_ids?: number[]; species?: PatientKind[] };
type PetSpecies = Pick<ClientDetailPatient, "id" | "species">;

const speciesOf = (pets: PetSpecies[], ids: number[]): PatientKind[] =>
  pets.filter((pet) => ids.includes(pet.id)).map((pet) => pet.species);

/** Selecting a pet adds its species; deselecting one removes only what no remaining pet has. */
export const speciesAfterPetSelection = (
  current: PetSearch,
  pets: PetSpecies[],
  next: number[],
): PatientKind[] => {
  const implied = new Set(speciesOf(pets, next));
  const released = new Set(
    speciesOf(
      pets,
      (current.patient_ids ?? []).filter((id) => !next.includes(id)),
    ),
  );
  const kept = (current.species ?? []).filter((kind) => implied.has(kind) || !released.has(kind));
  return [...kept, ...[...implied].filter((kind) => !kept.includes(kind))];
};

/** The label's tooltip has no hover to open it on small screens, so the sentence sits in the section instead. */
export const ForMyPetDescription = () => {
  const { isTabletLandscapeAndSmaller } = useBreakpoints();
  return isTabletLandscapeAndSmaller ? (
    <p className={styles.description}>{SCOPE_EXPLAINER}</p>
  ) : null;
};

export const ForMyPetInvitation = () => {
  const { isAuthenticated } = useAuthentication();
  const { open: openCreatePet } = useAccountCreatePetDrawer();
  const location = useLocation();

  return (
    <div className={styles.invitation}>
      {isAuthenticated ? (
        <>
          <TextButton
            type="button"
            hasUnderline={true}
            onClick={() => openCreatePet({}, { isAbovePresentation: true })}
          >
            Add a pet
          </TextButton>{" "}
          to find the best picks for them.
        </>
      ) : (
        <>
          <TextButton
            dest={urls.accounts.login({
              search: { next: location.current.href },
            })}
            hasUnderline={true}
          >
            Sign in
          </TextButton>{" "}
          to find the best picks for your pet.
        </>
      )}
    </div>
  );
};

export const ForMyPetLabel = () => {
  // Below this width the section lives in a modal, where the only thing to hover is its header.
  const { isTabletLandscapeAndSmaller } = useBreakpoints();

  return (
    <Tooltip
      content={SCOPE_EXPLAINER}
      isEnabled={!isTabletLandscapeAndSmaller}
      elementType="span"
      popoverClassName={styles.labelTooltip}
      maxWidth={240}
    >
      For my pet
    </Tooltip>
  );
};
